import { useState } from "react";
import { Phone, Mail, ArrowRight, Check } from "lucide-react";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  location: "",
  venue_name: "",
  event_date: "",
  estimated_attendance: "",
  service_type: "",
  message: "",
  company_website: "",
};

const REQUIRED = ["name", "email", "phone", "location", "service_type", "message"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function BookPage() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    REQUIRED.forEach((k) => {
      if (!form[k] || !form[k].trim()) next[k] = "Required";
    });
    if (form.email && !EMAIL_RE.test(form.email)) next.email = "Enter a valid email";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      await res.json();
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setServerError("Something went wrong sending your inquiry. Please try again, or call 980-298-0100.");
    }
  };

  const field = (name, label, type = "text", extra = {}) => (
    <div className={`field ${errors[name] ? "error" : ""}`}>
      <label htmlFor={name}>
        {label}
        {REQUIRED.includes(name) ? " *" : ""}
      </label>
      <input
        id={name}
        type={type}
        value={form[name]}
        onChange={set(name)}
        data-testid={`book-input-${name}`}
        {...extra}
      />
      {errors[name] && (
        <p className="field-error" data-testid={`book-error-${name}`}>
          {errors[name]}
        </p>
      )}
    </div>
  );

  return (
    <Layout padTop>
      <Seo
        title="Book a Show | Hustle Promotions"
        description="Book professional wrestling or stand-up comedy for your venue, business or private event. Tell Hustle Promotions what you're planning."
      />
      <section data-testid="book-page" style={{ background: "var(--black)" }}>
        <div className="container-editorial" style={{ paddingTop: "clamp(2.5rem, 6vw, 4rem)", paddingBottom: "clamp(1rem, 2vw, 1.5rem)" }}>
          <Reveal>
            <p className="eyebrow" style={{ marginBottom: "1.25rem" }}>Book A Show</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="h-page" data-testid="book-h1" style={{ maxWidth: "15ch" }}>Tell Us What You're Planning.</h1>
          </Reveal>
        </div>

        <div className="container-editorial section-pad" style={{ paddingTop: "clamp(1.5rem, 3vw, 2.5rem)" }}>
          <div className="book-layout">
            {/* Contact column */}
            <aside className="book-contact" data-testid="book-contact">
              <p className="eyebrow" style={{ marginBottom: "1.25rem" }}>Prefer To Talk?</p>
              <p className="body-md" style={{ maxWidth: "34ch", marginBottom: "2rem" }}>
                Reach Hustle Promotions directly and we'll walk through your event.
              </p>
              <a href="tel:+19802980100" className="book-contact-link" data-testid="book-phone-link">
                <Phone size={18} strokeWidth={1.6} /> 980-298-0100
              </a>
              <a href="mailto:Booking@HustlePromotionsLive.com" className="book-contact-link" data-testid="book-email-link">
                <Mail size={18} strokeWidth={1.6} /> Booking@HustlePromotionsLive.com
              </a>
              <div className="hairline" style={{ margin: "2rem 0" }} />
              <p className="font-cond" style={{ textTransform: "uppercase", letterSpacing: "0.14em", fontSize: "0.78rem", color: "var(--gray)", lineHeight: 1.7 }}>
                Professional Wrestling — North Carolina Only
                <br />
                Stand-Up Comedy — Contact For Availability
              </p>
            </aside>

            {/* Form column */}
            <div className="book-form-wrap">
              {status === "success" ? (
                <div className="confirm-panel" data-testid="book-success">
                  <div className="confirm-icon"><Check size={26} strokeWidth={2} /></div>
                  <h2 className="h-section" style={{ marginBottom: "1rem" }}>Inquiry Received.</h2>
                  <p className="body-lg" style={{ maxWidth: "42ch", marginBottom: "2rem" }}>
                    Hustle Promotions will be in touch to discuss your show.
                  </p>
                  <div className="flex flex-col sm:flex-row" style={{ gap: "1.5rem" }}>
                    <a href="tel:+19802980100" className="book-contact-link" style={{ margin: 0 }}>
                      <Phone size={18} strokeWidth={1.6} /> 980-298-0100
                    </a>
                    <a href="mailto:Booking@HustlePromotionsLive.com" className="book-contact-link" style={{ margin: 0 }}>
                      <Mail size={18} strokeWidth={1.6} /> Booking@HustlePromotionsLive.com
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="book-form">
                  {/* Honeypot */}
                  <div className="hp-field" aria-hidden="true">
                    <label htmlFor="company_website">Company Website</label>
                    <input id="company_website" type="text" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set("company_website")} />
                  </div>

                  <div className="form-grid two">
                    {field("name", "Name")}
                    {field("company", "Company / Organization")}
                    {field("email", "Email", "email")}
                    {field("phone", "Phone", "tel")}
                    {field("location", "Event Location")}
                    {field("venue_name", "Venue Name")}
                    {field("event_date", "Preferred Event Date", "date")}
                    {field("estimated_attendance", "Estimated Attendance")}
                  </div>

                  <div className="form-grid" style={{ marginTop: "clamp(1.1rem, 2.5vw, 1.5rem)" }}>
                    <div className={`field ${errors.service_type ? "error" : ""}`}>
                      <label htmlFor="service_type">Type Of Show *</label>
                      <select id="service_type" value={form.service_type} onChange={set("service_type")} data-testid="book-input-service_type">
                        <option value="">Select…</option>
                        <option value="wrestling">Professional Wrestling</option>
                        <option value="comedy">Comedy</option>
                        <option value="not_sure">Not Sure Yet</option>
                      </select>
                      {errors.service_type && <p className="field-error" data-testid="book-error-service_type">{errors.service_type}</p>}
                      {form.service_type === "wrestling" && (
                        <p className="nc-notice" data-testid="book-nc-notice">
                          Professional wrestling bookings are currently available within North Carolina only.
                        </p>
                      )}
                    </div>

                    <div className={`field ${errors.message ? "error" : ""}`}>
                      <label htmlFor="message">Tell Us About What You're Planning *</label>
                      <textarea id="message" value={form.message} onChange={set("message")} data-testid="book-input-message" placeholder="Location, venue, date, audience, and whether you're thinking wrestling or comedy." />
                      {errors.message && <p className="field-error" data-testid="book-error-message">{errors.message}</p>}
                    </div>
                  </div>

                  {status === "error" && (
                    <p className="field-error" data-testid="book-server-error" style={{ marginTop: "1.25rem", fontSize: "0.78rem" }}>
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-solid"
                    data-testid="book-submit"
                    disabled={status === "loading"}
                    style={{ marginTop: "2rem", width: "100%", opacity: status === "loading" ? 0.7 : 1 }}
                  >
                    {status === "loading" ? "Sending…" : (<>Send Inquiry <ArrowRight size={16} strokeWidth={2} /></>)}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
